import { faCheck } from "@fortawesome/duotone-regular-svg-icons/faCheck";
import { faUpload } from "@fortawesome/duotone-regular-svg-icons/faUpload";
import { faX } from "@fortawesome/duotone-regular-svg-icons/faX";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  CloseButton,
  FileButton,
  Group,
  Image,
  Modal,
  Progress,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { FunctionComponent, useState } from "react";
import { uploadCampgroundImage } from "../../api-clients/iwannacamp-api";
import { resizeImage } from "../../utils/images";
import { createLogger } from "../../utils/logger";
import { AmenityTagSelector } from "../shared/AmenityTagSelector";

const logger = createLogger("ImageUploadModal");

interface ImageUploadModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Campground SQID */
  campgroundSqid: string;
  /** Campground name for display */
  campgroundName: string;
}

const MAX_FILES = 10; // Maximum number of files
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

interface ImagePreview {
  file: File;
  previewUrl: string;
  amenities: string[];
  description: string;
  uploadStatus?: "pending" | "uploading" | "success" | "error";
  uploadProgress?: number;
  errorMessage?: string;
}

export const ImageUploadModal: FunctionComponent<ImageUploadModalProps> = ({
  opened,
  onClose,
  campgroundSqid,
  campgroundName,
}) => {
  const [selectedImages, setSelectedImages] = useState<ImagePreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    // Check if adding these files would exceed the limit
    if (selectedImages.length + files.length > MAX_FILES) {
      notifications.show({
        title: "Too Many Files",
        message: `Maximum ${MAX_FILES} images allowed. You have ${selectedImages.length} and tried to add ${files.length} more.`,
        color: "red",
      });
      return;
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }

      validFiles.push(file);
    }

    // Show errors if any
    if (errors.length > 0) {
      notifications.show({
        title: "Some Files Rejected",
        message: errors.join(", "),
        color: "orange",
      });
    }

    // Resize and create preview URLs for valid files
    for (const file of validFiles) {
      try {
        const originalSize = file.size;

        // Resize the image
        const resizedFile = await resizeImage(file);
        const resizedSize = resizedFile.size;

        // Create preview URL from resized file
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedImages((prev) => [
            ...prev,
            {
              file: resizedFile,
              previewUrl: reader.result as string,
              amenities: [],
              description: "",
            },
          ]);
        };
        reader.readAsDataURL(resizedFile);

        // Show size reduction notification if significant
        if (originalSize > resizedSize * 1.5) {
          const originalMB = (originalSize / 1024 / 1024).toFixed(1);
          const resizedMB = (resizedSize / 1024 / 1024).toFixed(1);
          logger.info(`Resized ${file.name}: ${originalMB}MB → ${resizedMB}MB`);
        }
      } catch (error) {
        logger.error(`Failed to resize ${file.name}:`, error);
        errors.push(`${file.name}: Failed to resize image`);
      }
    }

    // Show additional errors from resizing if any
    if (errors.length > 0) {
      notifications.show({
        title: "Some Files Failed",
        message: errors.join(", "),
        color: "red",
      });
    }
  };

  const handleUpload = async () => {
    if (selectedImages.length === 0) {
      return;
    }

    setIsUploading(true);

    try {
      let uploadedCount = 0;
      let failedCount = 0;

      // Upload images sequentially
      for (let i = 0; i < selectedImages.length; i++) {
        const imagePreview = selectedImages[i];

        try {
          // Mark as uploading
          setSelectedImages((prev) =>
            prev.map((img, idx) =>
              idx === i
                ? {
                    ...img,
                    uploadStatus: "uploading" as const,
                    uploadProgress: 30,
                  }
                : img
            )
          );

          // Prepare tags object for this image
          const tags: Record<string, any> = {};
          if (imagePreview.amenities.length > 0) {
            tags.amenities = imagePreview.amenities;
          }
          if (imagePreview.description.trim()) {
            tags.description = imagePreview.description.trim();
          }

          const response = await uploadCampgroundImage({
            path: { campground_sqid: campgroundSqid },
            body: {
              file: imagePreview.file,
              tags:
                Object.keys(tags).length > 0 ? JSON.stringify(tags) : undefined,
            },
          });

          // Check if the response has an error (API returned non-2xx status)
          if (response.error || !response.data) {
            throw new Error("Upload failed");
          }

          // Mark as success
          setSelectedImages((prev) =>
            prev.map((img, idx) =>
              idx === i
                ? {
                    ...img,
                    uploadStatus: "success" as const,
                    uploadProgress: 100,
                  }
                : img
            )
          );

          uploadedCount++;
        } catch (error) {
          logger.error(`Failed to upload ${imagePreview.file.name}:`, error);

          // Mark as error
          setSelectedImages((prev) =>
            prev.map((img, idx) =>
              idx === i
                ? {
                    ...img,
                    uploadStatus: "error" as const,
                    uploadProgress: 0,
                    errorMessage: "Upload failed",
                  }
                : img
            )
          );

          failedCount++;
        }
      }

      // Show success/failure notification
      if (failedCount === 0) {
        notifications.show({
          title: "Upload Successful",
          message: `${uploadedCount} image${
            uploadedCount > 1 ? "s" : ""
          } submitted and pending admin approval.`,
          color: "green",
        });

        // Reset form and close on full success
        setSelectedImages([]);
        onClose();
      } else if (uploadedCount > 0) {
        notifications.show({
          title: "Partial Upload",
          message: `${uploadedCount} succeeded, ${failedCount} failed. You can retry the failed images.`,
          color: "orange",
        });

        // Remove successful images, keep failed ones
        setSelectedImages((prev) =>
          prev.filter((img) => img.uploadStatus === "error")
        );
      } else {
        notifications.show({
          title: "Upload Failed",
          message: "All uploads failed. Please try again.",
          color: "red",
        });
      }
    } catch (error) {
      logger.error("Failed to upload images:", error);
      notifications.show({
        title: "Upload Failed",
        message: "Failed to upload images. Please try again.",
        color: "red",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setSelectedImages([]);
    onClose();
  };

  const updateImageTags = (
    index: number,
    field: "amenities" | "description",
    value: string[] | string
  ) => {
    setSelectedImages((prev) =>
      prev.map((img, idx) => (idx === index ? { ...img, [field]: value } : img))
    );
  };

  const removeImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Upload Images: ${campgroundName}`}
      size="lg"
      zIndex={310}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Share your photos to help other campers! Select up to {MAX_FILES}{" "}
          images. All images will be reviewed by admins before being displayed.
        </Text>

        <FileButton
          onChange={handleFileSelect}
          accept="image/png,image/jpeg,image/jpg,image/webp"
          multiple
          disabled={isUploading || selectedImages.length >= MAX_FILES}
        >
          {(props) => (
            <Button
              {...props}
              leftSection={<FontAwesomeIcon icon={faUpload} />}
              fullWidth
              disabled={isUploading || selectedImages.length >= MAX_FILES}
            >
              {selectedImages.length === 0
                ? "Select Images"
                : `Add More Images (${selectedImages.length}/${MAX_FILES})`}
            </Button>
          )}
        </FileButton>

        {selectedImages.length > 0 && (
          <>
            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Selected Images ({selectedImages.length})
                </Text>
                {!isUploading && (
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => setSelectedImages([])}
                  >
                    Clear All
                  </Button>
                )}
              </Group>

              <Stack gap="lg">
                {selectedImages.map((imagePreview, index) => (
                  <Box key={index}>
                    <Box pos="relative" mb="xs">
                      <Image
                        src={imagePreview.previewUrl}
                        alt={`Preview ${index + 1}`}
                        radius="md"
                        fit="cover"
                        h={200}
                        style={{
                          opacity:
                            imagePreview.uploadStatus === "success" ? 0.6 : 1,
                        }}
                      />
                      {!isUploading && (
                        <CloseButton
                          pos="absolute"
                          top={5}
                          right={5}
                          size="sm"
                          onClick={() => removeImage(index)}
                          style={{
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            color: "white",
                          }}
                        />
                      )}

                      {/* Status badge overlay */}
                      {imagePreview.uploadStatus === "success" && (
                        <Box
                          pos="absolute"
                          top={5}
                          left={5}
                          style={{
                            backgroundColor: "rgba(34, 139, 34, 0.9)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          <FontAwesomeIcon icon={faCheck} /> Uploaded
                        </Box>
                      )}

                      {imagePreview.uploadStatus === "error" && (
                        <Box
                          pos="absolute"
                          top={5}
                          left={5}
                          style={{
                            backgroundColor: "rgba(220, 53, 69, 0.9)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          <FontAwesomeIcon icon={faX} /> Failed
                        </Box>
                      )}
                    </Box>

                    <Text size="xs" c="dimmed" truncate mb="xs">
                      {imagePreview.file.name}
                    </Text>

                    {/* Per-image tags */}
                    <Stack gap="xs" mb="xs">
                      <div>
                        <Text size="xs" fw={500} mb={4}>
                          Amenities (optional)
                        </Text>
                        <AmenityTagSelector
                          value={imagePreview.amenities}
                          onChange={(amenities) =>
                            updateImageTags(index, "amenities", amenities)
                          }
                          placeholder="Select amenities in this image"
                          disabled={isUploading}
                        />
                      </div>

                      <Textarea
                        label="Description (optional)"
                        placeholder="e.g., picnic table with lake view..."
                        value={imagePreview.description}
                        onChange={(e) =>
                          updateImageTags(index, "description", e.target.value)
                        }
                        autosize
                        minRows={1}
                        maxRows={3}
                        maxLength={500}
                        disabled={isUploading}
                        size="xs"
                      />
                    </Stack>

                    {/* Individual progress bar */}
                    {imagePreview.uploadStatus &&
                      imagePreview.uploadStatus !== "pending" && (
                        <Progress
                          value={imagePreview.uploadProgress || 0}
                          size="sm"
                          color={
                            imagePreview.uploadStatus === "success"
                              ? "green"
                              : imagePreview.uploadStatus === "error"
                              ? "red"
                              : "blue"
                          }
                          animated={imagePreview.uploadStatus === "uploading"}
                        />
                      )}

                    {/* Error message */}
                    {imagePreview.errorMessage && (
                      <Text size="xs" c="red" mt={2}>
                        {imagePreview.errorMessage}
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}

        <Group justify="flex-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={selectedImages.length === 0 || isUploading}
            loading={isUploading}
          >
            Upload {selectedImages.length > 0 && `(${selectedImages.length})`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ImageUploadModal;
